import { useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import adminApi from '@/lib/adminApi';

const KALAOPS_FOUNDATION_NAME = 'KalaOps Foundation';
const KALAOPS_FOUNDATION_VERSION = 'v0.1.0';

const primaryTabs = [
  ['overview', 'Dashboard'],
  ['tasks', 'Tasks'],
  ['qa', 'QA'],
  ['agents', 'Agents'],
  ['logs', 'Logs'],
  ['settings', 'Settings'],
];

const detailSections = new Set(['registration', 'marketplace', 'seo', 'analytics']);

const endpointFor = (section) => {
  if (section === 'overview' || section === 'dashboard') return '/kalaops/dashboard';
  if (['qa', 'registration', 'marketplace', 'seo'].includes(section)) {
    return `/kalaops/monitors/${section}`;
  }
  return `/kalaops/${section}`;
};

const modeLabels = {
  live: 'Live data',
  simulation: 'Simulation Mode',
  unavailable: 'Not connected',
};

const PageHeading = ({ title, description, onBack, mode = 'unavailable' }) => (
  <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
    <div>
      {onBack && (
        <button type="button" onClick={onBack} className="mb-3 text-sm font-black text-[#FF6F00] hover:text-[#4A148C]">
          ← Back to Dashboard
        </button>
      )}
      <p className="text-xs font-black uppercase tracking-[0.24em] text-[#FF6F00]">KalaOps AI</p>
      <h2 className="mt-2 text-3xl font-black text-gray-950">{title}</h2>
      <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">{description}</p>
    </div>
    <span className={`w-fit rounded-full border px-4 py-2 text-xs font-black uppercase tracking-wider ${
      mode === 'live'
        ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
        : mode === 'simulation'
          ? 'border-orange-300 bg-orange-50 text-orange-800'
          : 'border-gray-300 bg-gray-100 text-gray-600'
    }`}>
      {modeLabels[mode] || modeLabels.unavailable}
    </span>
  </div>
);

const LoadingState = () => (
  <div className="rounded-3xl border border-orange-100 bg-white p-8 text-sm font-semibold text-gray-500 shadow-sm">
    Loading KalaOps workspace…
  </div>
);

const ErrorState = ({ message }) => (
  <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
    {message}
  </div>
);

const toneClasses = {
  healthy: 'border-emerald-300 bg-emerald-50 text-emerald-900',
  attention: 'border-yellow-300 bg-yellow-50 text-yellow-900',
  warning: 'border-orange-300 bg-orange-50 text-orange-900',
  critical: 'border-red-300 bg-red-50 text-red-900',
  neutral: 'border-gray-300 bg-gray-50 text-gray-700',
};

const statusToneClasses = {
  healthy: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  attention: 'border-yellow-300 bg-yellow-50 text-yellow-800',
  warning: 'border-orange-300 bg-orange-50 text-orange-800',
  critical: 'border-red-300 bg-red-50 text-red-800',
  disabled: 'border-gray-300 bg-gray-100 text-gray-600',
};

const overviewDestinations = {
  website: 'qa',
  api: 'qa',
  registration: 'registration',
  search: 'seo',
  marketplace: 'marketplace',
  payment: 'tasks',
  performance: 'qa',
  agents: 'agents',
  tasks: 'tasks',
};

// TODO (KalaOps Phase 2+):
// Replace these presentation metrics with registered agent dashboardWidgets() output.
const dashboardMetrics = {};
/*
  website: {
    label: 'Website Health',
    value: 'Healthy',
    detail: '99.98% availability',
    change: 'All monitored pages accessible',
    tone: 'healthy',
  },
  api: {
    label: 'API Health',
    value: '182 ms',
    detail: 'Average response time',
    change: '▼ 8% faster',
    tone: 'healthy',
  },
  registration: {
    label: "Today's Registrations",
    value: '82',
    detail: 'Completed registrations',
    change: '▲ 12% vs yesterday',
    tone: 'attention',
  },
  search: {
    label: 'Sitemap Entries',
    value: 'Not connected',
    detail: 'Sitemap declaration only',
    change: '▲ 3.1% this week',
    tone: 'healthy',
  },
  marketplace: {
    label: 'Orders Today',
    value: '24',
    detail: 'Marketplace orders',
    change: '▲ 8% vs yesterday',
    tone: 'healthy',
  },
  analytics: {
    label: 'Active Learners',
    value: '316',
    detail: 'Last 24 hours',
    change: '▲ 6% this week',
    tone: 'healthy',
  },
  payment: {
    label: "Today's Transactions",
    value: '31',
    detail: 'Payment monitoring pending',
    change: 'Live checks disabled',
    tone: 'neutral',
  },
  performance: {
    label: 'Performance',
    value: '92 / 100',
    detail: 'Platform experience score',
    change: 'Attention: mobile LCP',
    tone: 'attention',
  },
  tasks: {
    label: 'Pending Tasks',
    value: '4',
    detail: 'Shared agent queue',
    change: '2 require attention',
    tone: 'warning',
  },
  agents: {
    label: 'Active Agents',
    value: '0',
    detail: 'Foundation mode',
    change: '6 agents registered',
    tone: 'neutral',
  },
};
*/

const Overview = ({ data, onOpen }) => {
  const cards = useMemo(() => {
    const normalized = (data.cards || []).map((card) => ({
      ...(dashboardMetrics[card.key] || {}),
      ...card,
    }));
    /*
    const analyticsCard = {
      key: 'analytics',
      ...dashboardMetrics.analytics,
    };
    const paymentIndex = normalized.findIndex((card) => card.key === 'payment');
    normalized.splice(paymentIndex >= 0 ? paymentIndex : normalized.length, 0, analyticsCard);
    */
    return normalized;
  }, [data.cards]);

  return (
    <>
      <PageHeading
        title="Operations Dashboard"
        description="Only connected agents display operational numbers. Unconnected areas never show estimated or sample values."
        mode={data.mode}
      />
      <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => (
          <button
            key={card.key}
            type="button"
            onClick={() => onOpen(overviewDestinations[card.key] || card.key)}
            className={`group rounded-3xl border p-6 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-lg ${toneClasses[card.tone] || toneClasses.neutral}`}
          >
            <div className="flex items-start justify-between gap-3">
              <p className="text-xs font-black uppercase tracking-[0.18em] opacity-70">{card.label}</p>
              <span aria-hidden="true" className="text-lg transition group-hover:translate-x-1">→</span>
            </div>
            <p className="mt-4 text-2xl font-black">{card.value}</p>
            <p className="mt-2 text-sm opacity-75">{card.detail}</p>
            {card.metrics?.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-2 border-t border-current/15 pt-4">
                {card.metrics.map((metric) => (
                  <div key={metric.label} className="rounded-xl bg-white/60 px-3 py-2">
                    <p className="text-[10px] font-bold uppercase tracking-wide opacity-60">{metric.label}</p>
                    <p className="mt-1 text-sm font-black">{metric.value}</p>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-xs font-black uppercase tracking-wider opacity-80">{card.change}</p>
          </button>
        ))}
      </div>
    </>
  );
};

const MetricList = ({ title, items = [] }) => (
  <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
    <h3 className="text-lg font-black text-gray-950">{title}</h3>
    {items.length ? (
      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <div key={`${title}-${item.label}`} className="flex items-center justify-between gap-4 rounded-2xl bg-gray-50 px-4 py-3">
            <span className="text-sm font-semibold text-gray-600">{item.label}</span>
            <span className="font-black text-gray-950">{item.value}</span>
          </div>
        ))}
      </div>
    ) : (
      <p className="mt-4 text-sm font-semibold text-gray-500">This information is not currently collected.</p>
    )}
  </section>
);

const DetailValue = ({ label, value, href }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-3 py-3">
    <p className="text-[10px] font-black uppercase tracking-wider text-gray-400">{label}</p>
    {href && value ? (
      <a href={href} target="_blank" rel="noreferrer" className="mt-1 block break-all text-sm font-bold text-[#4A148C] underline decoration-purple-200 underline-offset-2">
        {value}
      </a>
    ) : (
      <p className="mt-1 break-words text-sm font-semibold text-gray-700">{value || 'Not present'}</p>
    )}
  </div>
);

const SeoAreaCard = ({ area }) => {
  const [expanded, setExpanded] = useState(false);
  const hasMetadata = area.url || area.http_status != null || area.title || area.description || area.canonical || area.h1_count != null || area.findings?.length;

  return (
    <article className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
      <button
        type="button"
        onClick={() => hasMetadata && setExpanded((current) => !current)}
        aria-expanded={expanded}
        className={`flex w-full items-center justify-between gap-4 px-4 py-4 text-left ${hasMetadata ? 'hover:bg-gray-100' : 'cursor-default'}`}
      >
        <div>
          <p className="font-black text-gray-950">{area.name}</p>
          <p className="mt-1 text-sm text-gray-600">{area.message}</p>
          {hasMetadata && <p className="mt-2 text-xs font-black uppercase tracking-wider text-[#4A148C]">{expanded ? 'Hide details' : 'View details'}</p>}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-black ${statusToneClasses[area.tone] || statusToneClasses.disabled}`}>
            {area.status}
          </span>
          {hasMetadata && <span aria-hidden="true" className="text-xl font-black text-[#4A148C]">{expanded ? '−' : '+'}</span>}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-gray-200 bg-white p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <DetailValue label="Public URL" value={area.url} href={area.url} />
            <DetailValue label="HTTP status" value={area.http_status == null ? 'Unavailable' : String(area.http_status)} />
            <DetailValue label="H1 count" value={area.h1_count == null ? 'Not checked for this file' : String(area.h1_count)} />
            {area.title !== undefined && <DetailValue label="Server title" value={area.title} />}
            {area.description !== undefined && <DetailValue label="Meta description" value={area.description} />}
            {area.canonical !== undefined && <DetailValue label="Canonical URL" value={area.canonical} href={area.canonical} />}
          </div>
          {area.findings?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-black uppercase tracking-wider text-gray-500">All findings</p>
              <ul className="mt-2 space-y-2">
                {area.findings.map((finding) => (
                  <li key={finding.code} className={`rounded-xl border px-3 py-2 text-sm font-semibold ${statusToneClasses[finding.tone] || statusToneClasses.disabled}`}>
                    {finding.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </article>
  );
};

const MonitorDetail = ({ data, onBack }) => {
  const isRegistration = data.key === 'registration';
  const isSeo = data.key === 'seo';
  return (
    <>
      <PageHeading title={data.title} description={data.summary} onBack={onBack} mode={data.mode} />
      {data.last_success_at && (
        <p className="mt-4 text-xs font-bold uppercase tracking-wider text-gray-500">
          Last successful scan: {new Date(data.last_success_at).toLocaleString()} · {data.timezone}
        </p>
      )}
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        {(data.metrics || []).map(([label, value]) => (
          <article key={label} className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
            <p className="text-sm font-semibold text-gray-500">{label}</p>
            <p className="mt-3 text-xl font-black text-gray-950">{value}</p>
          </article>
        ))}
      </div>
      {isSeo && (
        <section className="mt-6 rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-black text-gray-950">Observed public SEO surfaces</h3>
              <p className="mt-1 text-sm text-gray-500">Server-delivered metadata only. Sitemap membership is not proof of search-engine indexing.</p>
            </div>
            <span className="rounded-full border border-gray-300 bg-gray-50 px-3 py-1 text-xs font-black text-gray-600">READ ONLY</span>
          </div>
          <div className="mt-5 space-y-3">
            {(data.areas || []).map((area) => (
              <SeoAreaCard key={area.key} area={area} />
            ))}
          </div>
        </section>
      )}
      {isSeo && data.duplicate_titles?.length > 0 && (
        <section className="mt-6 rounded-3xl border border-orange-300 bg-orange-50 p-6 shadow-sm">
          <h3 className="text-lg font-black text-gray-950">Duplicate page titles</h3>
          <p className="mt-1 text-sm text-gray-600">These exact server-delivered titles appear on more than one monitored page.</p>
          <div className="mt-4 space-y-4">
            {data.duplicate_titles.map((group) => (
              <article key={group.title} className="rounded-2xl border border-orange-200 bg-white p-4">
                <p className="text-xs font-black uppercase tracking-wider text-orange-700">Repeated title</p>
                <p className="mt-1 font-black text-gray-950">{group.title}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(group.pages || []).map((page) => (
                    <a key={`${group.title}-${page.key || page.url}`} href={page.url} target="_blank" rel="noreferrer" className="rounded-full border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-bold text-[#4A148C] hover:bg-orange-100">
                      {page.name}: {page.url}
                    </a>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      {isRegistration && (
        <>
          <div className="mt-6 grid gap-6 xl:grid-cols-3">
            <MetricList title="Daily trend" items={data.trends?.daily} />
            <MetricList title="Weekly trend" items={data.trends?.weekly} />
            <MetricList title="Monthly trend" items={data.trends?.monthly} />
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-3">
            <MetricList title="Top cities this month" items={data.geography?.cities} />
            <MetricList title="States this month" items={data.geography?.states} />
            <MetricList title="Recorded source" items={data.sources} />
          </div>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <MetricList
              title="Email verification timing"
              items={[
                { label: 'Verified samples', value: data.email_verification_timing?.sample_size ?? 0 },
                { label: 'Average', value: data.email_verification_timing?.average_minutes == null ? 'Unavailable' : `${data.email_verification_timing.average_minutes} min` },
                { label: 'Median', value: data.email_verification_timing?.median_minutes == null ? 'Unavailable' : `${data.email_verification_timing.median_minutes} min` },
                { label: 'Slowest', value: data.email_verification_timing?.maximum_minutes == null ? 'Unavailable' : `${data.email_verification_timing.maximum_minutes} min` },
              ]}
            />
            <MetricList
              title="OTP verification this month"
              items={[
                { label: 'Requests', value: data.otp_statistics?.requests ?? 0 },
                { label: 'Verified', value: data.otp_statistics?.verified ?? 0 },
                { label: 'Pending', value: data.otp_statistics?.pending ?? 0 },
                { label: 'Expired', value: data.otp_statistics?.expired ?? 0 },
                { label: 'Failed attempts', value: data.otp_statistics?.failed_attempts ?? 0 },
                { label: 'Verification rate', value: data.otp_statistics?.verification_rate_percent == null ? 'Unavailable' : `${data.otp_statistics.verification_rate_percent}%` },
              ]}
            />
          </div>
        </>
      )}
      <section className="mt-6 rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
        <h3 className="text-lg font-black text-gray-950">Operational findings</h3>
        <div className="mt-4 space-y-3">
          {(data.findings || []).map((finding) => (
            <div key={finding} className="rounded-2xl bg-orange-50 px-4 py-3 text-sm font-semibold text-gray-700">{finding}</div>
          ))}
        </div>
      </section>
      {isRegistration && (
        <section className="mt-6 rounded-3xl border border-gray-200 bg-gray-50 p-6">
          <h3 className="text-lg font-black text-gray-950">Not currently measurable</h3>
          <p className="mt-2 text-sm text-gray-600">These values are deliberately not estimated.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {(data.unavailable_metrics || []).map((metric) => (
              <span key={metric} className="rounded-2xl border border-gray-300 bg-white px-3 py-2 text-xs font-bold text-gray-600">
                {metric} — Requires Event Tracking (Planned for Phase 2B)
              </span>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

const AnalyticsDetail = ({ data, onBack }) => (
  <>
    <PageHeading title={data.title} description={data.summary} onBack={onBack} mode={data.mode} />
    <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {(data.metrics || []).map((metric) => (
        <article key={metric.label} className="rounded-3xl border border-purple-100 bg-white p-6 shadow-sm">
          <p className="text-sm font-semibold text-gray-500">{metric.label}</p>
          <p className="mt-3 text-3xl font-black text-[#4A148C]">{metric.value}</p>
          <p className="mt-2 text-xs font-bold uppercase tracking-wider text-gray-400">{metric.note}</p>
        </article>
      ))}
    </div>
  </>
);

const TableShell = ({ children }) => (
  <div className="mt-8 overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-sm">
    <div className="overflow-x-auto">{children}</div>
  </div>
);

const priorityTone = {
  Critical: statusToneClasses.critical,
  Warning: statusToneClasses.warning,
  Attention: statusToneClasses.attention,
  Information: statusToneClasses.disabled,
  High: statusToneClasses.warning,
  Medium: statusToneClasses.attention,
  Low: statusToneClasses.disabled,
};

const taskStatusTone = {
  Open: statusToneClasses.warning,
  'In review': statusToneClasses.attention,
  Resolved: statusToneClasses.healthy,
  Ignored: statusToneClasses.disabled,
};

const Tasks = ({ data }) => {
  const [tasks, setTasks] = useState(data.items || []);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    setTasks(data.items || []);
  }, [data.items]);

  const updateTask = async (id, status) => {
    setActionError('');
    try {
      const response = await adminApi.patch(`/kalaops/tasks/${id}`, { status });
      setTasks((current) => current.map((task) => (task.id === id ? response.data : task)));
    } catch (requestError) {
      setActionError(requestError.response?.data?.detail || 'Task status could not be updated.');
    }
  };

  return (
    <>
      <PageHeading title="AI Task Center" description="Live deterministic findings created by connected KalaOps agents." mode="live" />
      {actionError && <ErrorState message={actionError} />}
      {!tasks.length && (
        <div className="mt-8 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 text-sm font-semibold text-emerald-800">
          No Registration Agent task has been created.
        </div>
      )}
      {!!tasks.length && (
      <TableShell>
        <table className="min-w-[1180px] w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>{['Title', 'Priority', 'Status', 'Source Agent', 'Created Time', 'Actions', 'Notes'].map((heading) => <th key={heading} className="px-5 py-4">{heading}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {tasks.map((task) => (
              <tr key={task.id} className="align-top">
                <td className="px-5 py-5 font-bold text-gray-950">{task.title}</td>
                <td className="px-5 py-5">
                  <span className={`rounded-full border px-3 py-1 font-bold ${priorityTone[task.priority] || statusToneClasses.disabled}`}>
                    {task.priority}
                  </span>
                </td>
                <td className="px-5 py-5">
                  <span className={`rounded-full border px-3 py-1 font-bold ${taskStatusTone[task.status] || statusToneClasses.disabled}`}>
                    {task.status}
                  </span>
                </td>
                <td className="px-5 py-5 text-gray-700">{task.source_agent}</td>
                <td className="px-5 py-5 whitespace-nowrap text-gray-500">{new Date(task.created_at).toLocaleString()}</td>
                <td className="px-5 py-5">
                  <div className="flex gap-2">
                    <button type="button" onClick={() => updateTask(task.id, 'In review')} className="rounded-xl border border-purple-200 px-3 py-2 font-bold text-[#4A148C]">Review</button>
                    <button type="button" onClick={() => updateTask(task.id, 'Ignored')} className="rounded-xl border border-gray-200 px-3 py-2 font-bold text-gray-600">Ignore</button>
                    <button type="button" onClick={() => updateTask(task.id, 'Resolved')} className="rounded-xl border border-emerald-200 px-3 py-2 font-bold text-emerald-700">Resolve</button>
                  </div>
                </td>
                <td className="max-w-xs px-5 py-5 text-gray-500">{task.notes}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableShell>
      )}
      <p className="mt-3 text-xs font-semibold text-gray-400">Review, Ignore, and Resolve update only KalaOps task records. Artist records are never modified.</p>
    </>
  );
};

const QA = ({ data }) => {
  const [expanded, setExpanded] = useState('');
  const areas = data.areas || [];

  return (
    <>
      <PageHeading title="QA Center" description={data.summary || 'QA Agent is not connected.'} mode={data.mode} />
      {!areas.length && (
        <div className="mt-8 rounded-3xl border border-gray-200 bg-gray-50 p-6 text-sm font-semibold text-gray-600">
          No live QA results are available. No health status has been inferred.
        </div>
      )}
      <div className="mt-8 space-y-3">
        {areas.map((area) => {
          const isOpen = expanded === area.key;
          return (
            <article key={area.key} className="overflow-hidden rounded-3xl border border-orange-100 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? '' : area.key)}
                className="flex w-full items-center justify-between gap-4 p-5 text-left"
              >
                <div>
                  <p className="font-black text-gray-950">{area.name}</p>
                  <span className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-black ${statusToneClasses[area.tone] || statusToneClasses.disabled}`}>
                    {area.status}
                  </span>
                </div>
                <span className="text-xl font-black text-[#4A148C]">{isOpen ? '−' : '+'}</span>
              </button>
              {isOpen && (
                <div className="border-t border-orange-100 bg-orange-50/60 px-5 py-4 text-sm text-gray-600">
                  <p>{area.message}</p>
                  <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
                    <div><dt className="font-black uppercase text-gray-400">HTTP status</dt><dd className="mt-1 font-bold text-gray-700">{area.http_status ?? 'Not observed'}</dd></div>
                    <div><dt className="font-black uppercase text-gray-400">Response time</dt><dd className="mt-1 font-bold text-gray-700">{area.response_ms == null ? 'Not observed' : `${area.response_ms} ms`}</dd></div>
                    <div><dt className="font-black uppercase text-gray-400">Checked</dt><dd className="mt-1 font-bold text-gray-700">{area.checked_at ? new Date(area.checked_at).toLocaleString() : 'Not observed'}</dd></div>
                  </dl>
                  <p className="mt-3 text-xs font-bold uppercase tracking-wider text-gray-400">Screenshot capture is reserved for a later QA phase.</p>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </>
  );
};

// TODO (KalaOps Phase 2+):
// Replace placeholder health values with each registered agent's health() output.
const agentHealth = {
  qa: ['Disabled', 'disabled'],
  registration: ['Disabled', 'disabled'],
  seo: ['Disabled', 'disabled'],
  marketplace: ['Disabled', 'disabled'],
  analytics: ['Disabled', 'disabled'],
  operations: ['Disabled', 'disabled'],
};

const Agents = ({ data }) => (
  <>
    <PageHeading title="Agent Registry" description="Agents register once with KalaOps Engine and automatically appear in this workspace." mode="live" />
    <TableShell>
      <table className="min-w-[980px] w-full text-left text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
          <tr>{['Agent Name', 'Status', 'Enabled', 'Version', 'Last Run', 'Health'].map((heading) => <th key={heading} className="px-5 py-4">{heading}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(data.items || []).map((agent) => {
            const [fallbackHealth, fallbackTone] = agentHealth[agent.key] || ['Disabled', 'disabled'];
            const healthLabel = agent.health || fallbackHealth;
            const healthTone = {
              Healthy: 'healthy',
              Attention: 'attention',
              Warning: 'warning',
              Critical: 'critical',
              Offline: 'critical',
              Disabled: 'disabled',
            }[healthLabel] || fallbackTone;
            return (
            <tr key={agent.key}>
              <td className="px-5 py-5">
                <p className="font-black text-gray-950">{agent.name}</p>
                <p className="mt-1 max-w-md text-xs text-gray-500">{agent.description}</p>
              </td>
              <td className="px-5 py-5"><span className="rounded-full border border-gray-300 bg-gray-50 px-3 py-1 font-bold text-gray-700">{agent.status}</span></td>
              <td className="px-5 py-5">
                <span className={`rounded-full border px-3 py-1 font-bold ${agent.enabled ? statusToneClasses.healthy : statusToneClasses.disabled}`}>
                  {agent.enabled ? 'Yes' : 'No'}
                </span>
              </td>
              <td className="px-5 py-5 font-mono text-gray-600">{agent.version}</td>
              <td className="px-5 py-5 text-gray-500">{agent.last_run}</td>
              <td className="px-5 py-5">
                <span className={`rounded-full border px-3 py-1 font-bold ${statusToneClasses[healthTone]}`}>
                  {healthLabel}
                </span>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </TableShell>
  </>
);

const Logs = ({ data }) => (
  <>
    <PageHeading title="Activity Timeline" description="A chronological record of all connected KalaOps agent activity." mode="live" />
    {!(data.items || []).length && (
      <div className="mt-8 rounded-3xl border border-gray-200 bg-gray-50 p-6 text-sm font-semibold text-gray-600">
        No live KalaOps activity has been recorded.
      </div>
    )}
    <div className="relative mt-8 space-y-4 before:absolute before:bottom-4 before:left-4 before:top-4 before:w-px before:bg-purple-200">
      {(data.items || []).map((entry) => (
        <article key={entry.id} className="relative ml-10 rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
          <span className={`absolute -left-[2.12rem] top-7 h-3 w-3 rounded-full ring-4 ${
            entry.status === 'Completed' ? 'bg-emerald-600 ring-emerald-100' : 'bg-gray-500 ring-gray-100'
          }`} />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="font-black text-gray-950">{entry.activity}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-[#4A148C]">{entry.agent}</p>
                <span className={`rounded-full border px-2.5 py-1 text-xs font-black ${
                  entry.status === 'Completed' ? statusToneClasses.healthy : statusToneClasses.disabled
                }`}>
                  {entry.status}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">{entry.detail}</p>
            </div>
            <time className="whitespace-nowrap text-xs font-semibold text-gray-400">{new Date(entry.created_at).toLocaleString()}</time>
          </div>
        </article>
      ))}
    </div>
  </>
);

const Settings = ({ data }) => (
  <>
    <PageHeading title="Settings" description="Read-only operational configuration. KalaOps agents cannot modify user records." mode={data.mode} />
    <section className="mt-8 grid gap-4 md:grid-cols-2">
      {[
        ['Mode', data.mode],
        ['Live operations enabled', data.live_operations_enabled ? 'Yes' : 'No'],
        ['Task retention', `${data.task_retention_days} days`],
        ['Activity log retention', `${data.activity_log_retention_days} days`],
        ['Default priority', data.default_priority],
        ['Registration scan interval', data.registration_scan_interval_minutes ? `${data.registration_scan_interval_minutes} minutes` : 'Not connected'],
        ['Registration shadow mode', data.registration_shadow_mode == null ? 'Not connected' : data.registration_shadow_mode ? 'Yes' : 'No'],
        ['QA scan interval', data.qa_scan_interval_minutes ? `${data.qa_scan_interval_minutes} minutes` : 'Not connected'],
        ['QA shadow mode', data.qa_shadow_mode == null ? 'Not connected' : data.qa_shadow_mode ? 'Yes' : 'No'],
        ['SEO scan interval', data.seo_scan_interval_minutes ? `${data.seo_scan_interval_minutes} minutes` : 'Not connected'],
        ['SEO shadow mode', data.seo_shadow_mode == null ? 'Not connected' : data.seo_shadow_mode ? 'Yes' : 'No'],
      ].map(([label, value]) => (
        <div key={label} className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
          <p className="text-sm font-semibold text-gray-500">{label}</p>
          <p className="mt-2 text-xl font-black capitalize text-gray-950">{value}</p>
        </div>
      ))}
    </section>
    <p className="mt-6 rounded-3xl border border-purple-100 bg-purple-50 p-5 text-sm font-semibold text-[#4A148C]">{data.notice}</p>
  </>
);

const suggestedQuestions = [
  'What is the platform health?',
  'Are there critical QA issues?',
  'How many registrations happened today?',
  'How many profiles are incomplete?',
  'Are there registration problems?',
  'What changed from yesterday?',
  'When was registration last checked?',
  'What is the SEO health?',
  'How many sitemap entries were found?',
  'Are any pages missing metadata?',
];

const AssistantPanel = ({ summary, onOpen }) => {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('Ask about platform health, tasks, registrations, or today’s activity.');
  const [recommendationVisible, setRecommendationVisible] = useState(true);
  const [asking, setAsking] = useState(false);

  const askKalaOps = async (prompt) => {
    const cleanPrompt = prompt.trim();
    if (!cleanPrompt) return;
    setQuestion(cleanPrompt);
    setAsking(true);
    try {
      const response = await adminApi.get('/kalaops/assistant', { params: { question: cleanPrompt } });
      setAnswer(response.data.answer);
    } catch (requestError) {
      setAnswer(requestError.response?.data?.detail || 'KalaOps could not retrieve a live answer.');
    } finally {
      setAsking(false);
    }
  };

  return (
    <aside className="h-fit rounded-[2rem] border border-purple-200 bg-gradient-to-b from-[#4A148C] to-[#6A1B9A] p-5 text-white shadow-xl lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
      <div className="flex items-center gap-3 border-b border-white/20 pb-5">
        <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/15 text-xl">🤖</div>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-200">KalaOps Assistant</p>
          <p className="mt-1 text-sm text-purple-100">Admin operations briefing</p>
        </div>
      </div>

      <p className="mt-5 text-lg font-black">Registration operations briefing</p>
      {/* TODO (KalaOps Phase 2+):
          Replace assistant briefing values with agent reports aggregated by KalaOps Engine. */}
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
        {[
          ['Registration Health', summary.platform_health || 'Unavailable'],
          ["Today's Summary", summary.today_summary || 'Registration data unavailable'],
          ['Active Issues', summary.active_issues ?? 'Unknown'],
          ['Pending Tasks', summary.pending_tasks ?? 0],
          ['Recent Alerts', summary.recent_alert || 'No live alert available'],
        ].map(([label, value]) => (
          <div key={label} className="rounded-2xl bg-white/10 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-purple-200">{label}</p>
            <p className="mt-1 font-black">{value}</p>
          </div>
        ))}
      </div>

      {/* TODO (KalaOps Phase 8):
          Replace the recommendation and confidence with live KalaOps Assistant output. */}
      {recommendationVisible ? (
        <div className="mt-4 rounded-2xl border border-orange-300/60 bg-orange-400/20 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-black uppercase tracking-wider text-orange-100">Deterministic recommendation</p>
              <p className="mt-2 text-sm font-bold leading-6">{summary.recommendation || 'No live recommendation is available.'}</p>
            </div>
            {summary.confidence != null && (
            <div className="rounded-xl border border-emerald-300 bg-emerald-950/40 px-3 py-2 text-right">
              <p className="text-[10px] font-black uppercase tracking-wider text-emerald-200">Confidence</p>
              <p className="text-lg font-black text-white">{summary.confidence}%</p>
            </div>
            )}
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => onOpen('tasks')} className="rounded-xl bg-white px-3 py-2 text-xs font-black text-[#4A148C]">Open Task</button>
            <button type="button" onClick={() => onOpen('tasks')} className="rounded-xl border border-white/40 px-3 py-2 text-xs font-black text-white">Review</button>
            <button type="button" onClick={() => onOpen('qa')} className="rounded-xl border border-white/40 px-3 py-2 text-xs font-black text-white">Run QA</button>
            <button type="button" onClick={() => setRecommendationVisible(false)} className="rounded-xl border border-white/20 px-3 py-2 text-xs font-black text-purple-100">Dismiss</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setRecommendationVisible(true)} className="mt-4 w-full rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm font-bold text-purple-100">
          Recommendation dismissed · Restore
        </button>
      )}

      <section className="mt-5 border-t border-white/20 pt-5" aria-labelledby="ask-kalaops-title">
        <h3 id="ask-kalaops-title" className="text-sm font-black">Ask KalaOps</h3>
        <form
          className="mt-3"
          onSubmit={(event) => {
            event.preventDefault();
            askKalaOps(question);
          }}
        >
          <label htmlFor="ask-kalaops-input" className="sr-only">Ask about platform health</label>
          <textarea
            id="ask-kalaops-input"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Ask about platform health..."
            rows={3}
            className="w-full resize-none rounded-2xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-purple-200 focus:border-orange-300 focus:outline-none"
          />
          <button type="submit" className="mt-2 w-full rounded-xl bg-[#FF6F00] px-4 py-2.5 text-sm font-black text-white transition hover:bg-orange-600">
            {asking ? 'Checking live data...' : 'Send'}
          </button>
        </form>
        <div className="mt-3 rounded-2xl bg-black/15 p-4" aria-live="polite">
          <p className="text-xs font-black uppercase tracking-wider text-purple-200">KalaOps response</p>
          <p className="mt-2 text-sm leading-6 text-white">{answer}</p>
        </div>
        <p className="mt-4 text-xs font-black uppercase tracking-wider text-purple-200">Suggested questions</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {suggestedQuestions.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => askKalaOps(prompt)}
              className="rounded-full border border-white/20 bg-white/10 px-3 py-2 text-left text-xs font-semibold text-purple-50 transition hover:bg-white/20"
            >
              {prompt}
            </button>
          ))}
        </div>
        <p className="mt-4 text-xs leading-5 text-purple-200">
          {summary.mode === 'live'
            ? 'Responses use the latest persisted Registration Agent snapshot.'
            : summary.mode === 'simulation'
              ? 'Simulation Mode is active; these responses are not production statistics.'
              : 'No connected agent data is available. KalaOps will not estimate an answer.'}
        </p>
      </section>
    </aside>
  );
};

// TODO (KalaOps Phase 2+):
// Replace notification counts and entries with events emitted by registered agents.
const NotificationCenter = ({ summary }) => {
  const [isOpen, setIsOpen] = useState(false);
  const counts = summary.notification_counts || { critical: 0, warning: 0, info: 0 };
  const total = counts.critical + counts.warning + counts.info;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-black text-gray-800 shadow-sm transition hover:border-orange-300"
      >
        <span aria-hidden="true">🔔</span>
        <span>Notifications</span>
        <span className={`rounded-full px-2 py-0.5 text-xs text-white ${total ? 'bg-red-600' : 'bg-gray-500'}`}>{total}</span>
      </button>
      {isOpen && (
        <div className="absolute right-0 z-30 mt-3 w-72 rounded-3xl border border-gray-200 bg-white p-4 shadow-2xl">
          <div className="flex items-center justify-between">
            <p className="font-black text-gray-950">Notification Center</p>
            <button type="button" onClick={() => setIsOpen(false)} className="text-sm font-black text-gray-500" aria-label="Close notifications">×</button>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2">
            {[
              ['Critical', counts.critical, statusToneClasses.critical],
              ['Warnings', counts.warning, statusToneClasses.warning],
              ['Info', counts.info, statusToneClasses.disabled],
            ].map(([label, count, tone]) => (
              <div key={label} className={`rounded-2xl border p-3 text-center ${tone}`}>
                <p className="text-xl font-black">{count}</p>
                <p className="mt-1 text-[10px] font-black uppercase tracking-wider">{label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 text-xs">
            <p className="rounded-xl bg-gray-100 p-3 font-semibold text-gray-700">
              {summary.recent_alert || 'No live Registration Agent alert is available.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

const renderSection = (section, data, navigate) => {
  if (section === 'overview') return <Overview data={data} onOpen={(destination) => navigate(`/admin/kalaops/${destination}`)} />;
  if (detailSections.has(section) && section !== 'analytics') return <MonitorDetail data={data} onBack={() => navigate('/admin/kalaops/overview')} />;
  if (section === 'analytics') return <AnalyticsDetail data={data} onBack={() => navigate('/admin/kalaops/overview')} />;
  if (section === 'tasks') return <Tasks data={data} />;
  if (section === 'qa') return <QA data={data} />;
  if (section === 'agents') return <Agents data={data} />;
  if (section === 'logs') return <Logs data={data} />;
  if (section === 'settings') return <Settings data={data} />;
  return null;
};

const AdminKalaOps = () => {
  const { section = 'overview' } = useParams();
  const navigate = useNavigate();
  const allowedSections = useMemo(() => new Set([...primaryTabs.map(([key]) => key), ...detailSections, 'dashboard']), []);
  const requestedSection = allowedSections.has(section) ? section : 'overview';
  const activeSection = requestedSection === 'dashboard' ? 'overview' : requestedSection;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [assistantSummary, setAssistantSummary] = useState({
    mode: 'unavailable',
    platform_health: 'Unavailable',
    today_summary: 'Registration data unavailable',
    active_issues: 'Unknown',
    pending_tasks: 0,
    recent_alert: 'No live alert available',
    notification_counts: { critical: 0, warning: 0, info: 0 },
  });

  useEffect(() => {
    let mounted = true;
    adminApi.get('/kalaops/dashboard')
      .then((response) => {
        if (mounted && response.data?.assistant) setAssistantSummary(response.data.assistant);
      })
      .catch(() => {
        // The assistant remains explicitly unavailable; no fallback values are invented.
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    setData(null);
    setError('');

    adminApi.get(endpointFor(activeSection))
      .then((response) => {
        if (mounted) {
          setData(response.data);
          if (activeSection === 'overview' && response.data?.assistant) setAssistantSummary(response.data.assistant);
        }
      })
      .catch((requestError) => {
        if (mounted) setError(requestError.response?.data?.detail || 'KalaOps data could not be loaded.');
      });

    return () => {
      mounted = false;
    };
  }, [activeSection]);

  return (
    <div className="space-y-6">
      <header className="rounded-[2rem] border border-orange-100 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Admin only</p>
              <h1 className="mt-1 text-2xl font-black text-gray-950">🤖 KalaOps AI Workspace</h1>
              <p className="mt-2 text-xs font-bold uppercase tracking-wider text-gray-500">
                {KALAOPS_FOUNDATION_NAME} · {KALAOPS_FOUNDATION_VERSION}
              </p>
            </div>
            <NotificationCenter summary={assistantSummary} />
          </div>
          <nav aria-label="KalaOps AI workspace tabs" className="flex gap-2 overflow-x-auto">
            {primaryTabs.map(([key, label]) => (
              <NavLink
                key={key}
                to={`/admin/kalaops/${key}`}
                className={({ isActive }) => `shrink-0 rounded-2xl px-4 py-2.5 text-sm font-bold transition ${
                  isActive
                    ? 'bg-gradient-to-r from-[#FF6F00] to-[#4A148C] text-white shadow'
                    : 'text-gray-600 hover:bg-orange-50'
                }`}
              >
                {label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_22rem]">
        <main className="min-w-0 rounded-[2rem] border border-orange-100 bg-white/70 p-5 shadow-sm sm:p-8">
          {error ? <ErrorState message={error} /> : data ? renderSection(activeSection, data, navigate) : <LoadingState />}
        </main>
        <AssistantPanel summary={assistantSummary} onOpen={(destination) => navigate(`/admin/kalaops/${destination}`)} />
      </div>
    </div>
  );
};

export default AdminKalaOps;
